import { DocumentDto } from '@app/generated/model';

export type DocumentCategory = 'JOB_PACKAGE' | 'ATTACHMENT' | 'DOC' | 'MODEL_3D' | 'VIDEO';
export type LegacyDocumentCategory = 'MEDIA';
export type DocumentSelectionCategory = DocumentCategory | LegacyDocumentCategory;

export const DOCUMENT_CATEGORIES: DocumentCategory[] = ['JOB_PACKAGE', 'ATTACHMENT', 'DOC', 'MODEL_3D', 'VIDEO'];

/** Categories also match documents added to the work order after the selection was made. */
export interface DocumentDownloadSelection {
  categories: DocumentSelectionCategory[];
  documentIds: string[];
}

export type DownloadStatus = 'queued' | 'downloading' | 'done' | 'failed';

export interface WorkOrderDownloadOptions {
  /** Omitted means every document is downloaded. */
  selection?: DocumentDownloadSelection;
  onDocumentStatus?: (documentId: string, status: DownloadStatus) => void;
}

const MODEL_3D_FILE_TYPES = ['FBX', 'GLB', 'GLTF'];
const VIDEO_FILE_TYPES = ['MP4', 'MOV', 'WEBM', 'AVI'];

export function getDocumentCategory(doc: DocumentDto): DocumentCategory {
  const fileType = doc.fileType?.toUpperCase() ?? '';
  if (doc.documentType === DocumentDto.DocumentTypeEnum.Model3D || MODEL_3D_FILE_TYPES.includes(fileType)) {
    return 'MODEL_3D';
  }
  if (doc.documentType === DocumentDto.DocumentTypeEnum.Video || VIDEO_FILE_TYPES.includes(fileType)) {
    return 'VIDEO';
  }
  if (doc.documentType === DocumentDto.DocumentTypeEnum.Link) {
    return 'DOC';
  }
  if (doc.documentType === DocumentDto.DocumentTypeEnum.JobPackage) {
    return 'JOB_PACKAGE';
  }
  return 'ATTACHMENT';
}

/** Expands the former combined media category when restoring selections saved by older clients. */
export function normalizeDocumentCategories(categories: readonly DocumentSelectionCategory[]): DocumentCategory[] {
  const normalized = new Set<DocumentCategory>();
  for (const category of categories) {
    if (category === 'MEDIA') {
      normalized.add('MODEL_3D');
      normalized.add('VIDEO');
    } else {
      normalized.add(category);
    }
  }
  return DOCUMENT_CATEGORIES.filter((category) => normalized.has(category));
}

export function isDocumentSelected(doc: DocumentDto, selection: DocumentDownloadSelection): boolean {
  const category = getDocumentCategory(doc);
  const isLegacyMediaSelection =
    (category === 'MODEL_3D' || category === 'VIDEO') && selection.categories.includes('MEDIA');
  return (
    selection.categories.includes(category) || isLegacyMediaSelection || selection.documentIds.includes(doc.id ?? '')
  );
}
